import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import Icon from '@/components/icons/Icon';
import { IconVariant } from '@/components/icons/types';
import { FINNISH_TRANSLATION_FEATURE_FLAG } from '@/constants/experimentConstants';
import { classnames } from '@/helpers';
import { useAppSelector } from '@/hooks';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { setLang } from '@/locale';
import { _t, getLang, getLanguages, Lang } from '@/locale/i18n';
import { useState } from 'react';
import { baseTranslationKey } from '../Footer.constants';

const LanguageSwitcher = () => {
  const [selectedLang] = useState<Lang>(getLang() as Lang);
  const [showOptions, setShowOptions] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const getVariant = useGetAmplitudeExperimentVariant();
  const flags = useAppSelector((state) => state.flags);
  const finnishTranslationFeature =
    flags[FINNISH_TRANSLATION_FEATURE_FLAG]?.value === 'on' ||
    getVariant(FINNISH_TRANSLATION_FEATURE_FLAG)?.value === 'on';

  const changeLanguage = (lang: Lang) => {
    setLang(lang);
  };

  const iconMap: { [key in Lang]: IconVariant } = {
    sv: 'swedish',
    en: 'english',
    fi: 'finnish',
  };

  const dropdownItemBaseClassnames = 'gap-xs flex items-center h-3xl px-sm rounded-sm';
  const languages = getLanguages().filter((lang) => lang !== 'fi' || finnishTranslationFeature);

  return (
    <div
      role="group"
      className={'w-[160px] sm:w-[200px]'}
      aria-label={_t(`${baseTranslationKey}.language-switcher.aria-label`)}>
      <DropdownMenu initialState={{ isOpen: showOptions }} onStateChange={(setOpen) => setShowOptions(setOpen)}>
        <DropdownMenu.Trigger asChild>
          <button
            className={classnames(
              'gap-xs px-sm py-xxs flex h-[40px] w-full items-center',
              'border-border_regular rounded-full outline-none',
              'hover:bg-bg_secondary_hover hover:border-border_bold text-fg_primary',
              `
              focus:ring-border_focus 
              focus:ring-[3px]
              `,
              `
              active:text-fg_alwaysLight
              active:bg-bg_info_bold
              active:border-none
              active:ring-0
              `,
              `
              data-[state=open]:ring-border_focus 
              data-[state=open]:rounded-md 
              data-[state=closed]:border
              data-[state=open]:ring-[3px]
              [&[data-state=open][data-placement=bottom]]:rounded-bl-none [&[data-state=open][data-placement=bottom]]:rounded-br-none
              [&[data-state=open][data-placement=bottom]]:border-b [&[data-state=open][data-placement=top]]:rounded-tl-none
              [&[data-state=open][data-placement=top]]:rounded-tr-none
              [&[data-state=open][data-placement=top]]:border-t
          `,
            )}
            onMouseDown={() => setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
            onMouseLeave={() => setIsPressed(false)}>
            <Icon variant={iconMap[selectedLang]} noFilter />
            <span className="text-l flex-1 text-left text-current">{_t(`${selectedLang}Short`)}</span>
            <Icon
              color={isPressed ? 'fg_alwaysLight' : 'fg_primary'}
              variant={showOptions ? 'chevron-up' : 'chevron-down'}
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          className={classnames(
            'p-xs space-y-2xs bg-bg_neutral w-[166px] sm:w-[206px]',
            'border-border_focus -mx-[3px] rounded-md border-[3px]',
            `
            data-[placement=bottom]:rounded-tl-none data-[placement=bottom]:rounded-tr-none
            data-[placement=top]:rounded-bl-none data-[placement=top]:rounded-br-none
            data-[placement=bottom]:border-t-0 data-[placement=top]:border-b-0
            `,
          )}>
          {languages.map((lang) => {
            const isActive = lang === getLang();

            return (
              <DropdownMenu.Item
                onSelect={() => changeLanguage(lang)}
                className={classnames(dropdownItemBaseClassnames, isActive && 'bg-bg_info_bold')}
                key={lang}>
                <Icon variant={iconMap[lang]} noFilter />
                <span className="text-fg_primary text-l flex-1 text-left">{_t(`${lang}Short`)}</span>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu>
    </div>
  );
};

export default LanguageSwitcher;
